import { gql } from "urql";
import type { Cache, FieldInfo } from "~/cache/graphcache/src";
import { ProjectTasksList_DataDocument } from "~/gql/graphql";

export type Validation = string | null;

export const updateTaskFragment = (
  cache: Cache,
  taskId: string,
  newStatus: Validation
) => {
  cache.writeFragment(
    gql`
      fragment _ on tasks {
        id
        is_validated_by
      }
    `,
    { id: taskId, is_validated_by: newStatus }
  );
};

export const updateTasksList = (
  cache: Cache,
  taskId: string,
  newStatus: Validation,
  queryArgs: Record<string, any>,
  projectId: string
) => {
  cache.updateQuery(
    {
      query: ProjectTasksList_DataDocument,
      //@ts-expect-error – todo: fix this
      variables: { ...queryArgs, projectId },
    },
    (data: unknown) => {
      if (!data || typeof data !== "object" || !("tasks" in data)) {
        return data;
      }

      const typedData = data as {
        tasks: Array<{ id: string; is_validated_by?: string }>;
      };

      const updatedTasks = typedData.tasks.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            is_validated_by: newStatus,
          };
        }
        return task;
      });

      return {
        ...data,
        tasks: updatedTasks,
      };
    }
  );
};

export const updateProjectByPkTasks = (
  cache: Cache,
  taskId: string,
  newStatus: Validation,
  queryArgs: Record<string, any>,
  projectId: string
) => {
  const queryProjectId = queryArgs?.projectId as string;

  if (queryProjectId === projectId) {
    try {
      cache.updateQuery(
        {
          query: ProjectTasksList_DataDocument,
          //@ts-expect-error – todo: fix this
          variables: { projectId: queryProjectId },
        },
        (data: unknown) => {
          if (
            !data ||
            typeof data !== "object" ||
            !("projects_by_pk" in data)
          ) {
            console.log(
              "🔥 urql:updateValidationTask: no projects_by_pk data found for immediate update"
            );
            return data;
          }

          const typedData = data as {
            projects_by_pk: {
              tasks?: Array<{
                id: string;
                is_validated_by?: string;
              }>;
            };
          };

          if (!typedData.projects_by_pk?.tasks) {
            console.log(
              "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: no tasks found in projects_by_pk for immediate update"
            );
            return data;
          }

          const updatedTasks = typedData.projects_by_pk.tasks.map((task) => {
            if (task.id === taskId) {
              console.log(
                "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: updating task in projects_by_pk list",
                {
                  newStatus,
                  oldStatus: task.is_validated_by,
                  taskId: task.id,
                }
              );
              return {
                ...task,
                is_validated_by: newStatus,
              };
            }
            return task;
          });

          return {
            ...data,
            projects_by_pk: {
              ...typedData.projects_by_pk,
              tasks: updatedTasks,
            },
          };
        }
      );
    } catch (error) {
      console.log(
        "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: error updating projects_by_pk query",
        error
      );
    }
  } else {
    console.log(
      "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: skipping projects_by_pk query for different project",
      {
        queryProjectId,
        targetProjectId: projectId,
        taskId,
      }
    );
  }
};

export const updateTaskByPk = (
  cache: Cache,
  taskId: string,
  newStatus: Validation,
  queryArgs: Record<string, any>
) => {
  const queryTaskId = (queryArgs as { id: string })?.id;
  if (queryTaskId === taskId) {
    updateTaskFragment(cache, taskId, newStatus);
  }
};

export const toggleValidationStatus = (prev?: string | null): Validation =>
  prev && prev !== "none" ? "none" : "user";

export const getCurrentValidationStatus = (
  cache: Cache,
  taskId: string
): { id: string; is_validated_by?: string } | null => {
  return cache.readFragment(
    gql`
      fragment _ on tasks {
        id
        is_validated_by
      }
    `,
    { id: taskId }
  ) as { id: string; is_validated_by?: string } | null;
};

export const filterTaskQueries = (queries: FieldInfo[]) => {
  return queries.filter((query) => {
    const isTaskQuery =
      query.fieldName === "tasks" ||
      query.fieldName === "tasks_by_pk" ||
      query.fieldName === "projects_by_pk";

    return isTaskQuery;
  });
};

export const updateTaskValidationInCache = (
  cache: Cache,
  taskId: string,
  projectId: string
) => {
  const existing = getCurrentValidationStatus(cache, taskId);

  if (!existing) {
    console.log(
      "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: task fragment not found",
      { taskId }
    );
    return;
  }

  const newStatus = toggleValidationStatus(existing.is_validated_by);

  if (existing.is_validated_by !== newStatus) {
    updateTaskFragment(cache, taskId, newStatus);

    const queries = cache.inspectFields("Query");
    const taskQueries = filterTaskQueries(queries);

    for (const query of taskQueries) {
      const queryArgs = query.arguments ?? {};

      switch (query.fieldName) {
        case "tasks":
          updateTasksList(cache, taskId, newStatus, queryArgs, projectId);
          break;

        case "projects_by_pk":
          updateProjectByPkTasks(
            cache,
            taskId,
            newStatus,
            queryArgs,
            projectId
          );
          break;

        case "tasks_by_pk":
          updateTaskByPk(cache, taskId, newStatus, queryArgs);
          break;
      }
    }
  } else {
    console.log(
      "🔥 [OFFLINE_VALIDATION] urql:updateValidationTask: skipping update - status already correct",
      {
        currentStatus: existing.is_validated_by,
        taskId,
      }
    );
  }
};
